// ═══════════════════════════════════════════════════════════════════════════
// ACCOUNTS — basic email+password auth gating the per-account watchlists.
// (Backend is OAuth-ready; this is the minimal client for the password flow.)
//
// Phase 5 Step 11: extracted from app.js via dependency injection. App-shell unit
// (cookie-based auth, DOM modal, legacy-migration) — its browser globals (fetch,
// document, localStorage migration keys, window, alert/confirm) belong HERE, not in
// the portable state/engine core. Reaches into app.js (the 4 watchlist-membership
// render fns) are injected via configure(); the Watchlist singleton is imported.
// ═══════════════════════════════════════════════════════════════════════════
import { Watchlist } from './watchlist.js';

let _deps = {};
export function configure(d) { _deps = { ..._deps, ...d }; }

export const Auth = (() => {
            let user = null;
            const isAuthed = () => !!user;
            const getUser = () => user;

            async function api(method, path, body) {
                const res = await fetch(path, {
                    method, credentials: 'same-origin',
                    headers: body ? { 'Content-Type': 'application/json' } : {},
                    body: body ? JSON.stringify(body) : undefined
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) { const e = new Error((data && data.error) || ('http ' + res.status)); e.status = res.status; throw e; }
                return data;
            }

            // ── Modal ──────────────────────────────────────────────────────────────
            let modal = null, mode = 'login';
            function buildModal() {
                modal = document.createElement('div');
                modal.id = 'authModal';
                modal.classList.add('hidden');
                modal.innerHTML = `
                    <div class="auth-card">
                        <div class="flex items-center justify-between mb-1">
                            <div id="authTitle" class="text-white font-black text-sm">Sign in</div>
                            <button id="authClose" class="text-slate-500 hover:text-slate-200 text-lg leading-none">&times;</button>
                        </div>
                        <div class="text-slate-500 text-[11px]" id="authSub">Welcome back — sign in to your watchlists.</div>
                        <input id="authEmail" type="email" placeholder="you@example.com" autocomplete="username">
                        <input id="authPassword" type="password" placeholder="Password (min 8 chars)" autocomplete="current-password">
                        <div class="auth-err" id="authErr"></div>
                        <button class="auth-submit" id="authSubmit">Sign in</button>
                        <div class="text-[11px] text-slate-500 mt-3 text-center">
                            <span id="authSwitchText">No account?</span>
                            <span class="auth-toggle" id="authSwitch">Create one</span>
                        </div>
                    </div>`;
                document.body.appendChild(modal);
                modal.addEventListener('mousedown', (e) => { if (e.target === modal) closeModal(); });
                modal.querySelector('#authClose').addEventListener('click', closeModal);
                modal.querySelector('#authSwitch').addEventListener('click', () => setMode(mode === 'login' ? 'register' : 'login'));
                modal.querySelector('#authSubmit').addEventListener('click', submit);
                modal.querySelector('#authPassword').addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
                modal.querySelector('#authEmail').addEventListener('keydown', (e) => { if (e.key === 'Enter') modal.querySelector('#authPassword').focus(); });
            }
            function setMode(m) {
                mode = m;
                modal.querySelector('#authTitle').textContent = m === 'login' ? 'Sign in' : 'Create account';
                modal.querySelector('#authSub').textContent = m === 'login' ? 'Welcome back — sign in to your watchlists.' : 'Create an account to save cloud watchlists.';
                modal.querySelector('#authSubmit').textContent = m === 'login' ? 'Sign in' : 'Create account';
                modal.querySelector('#authSwitchText').textContent = m === 'login' ? 'No account?' : 'Already have one?';
                modal.querySelector('#authSwitch').textContent = m === 'login' ? 'Create one' : 'Sign in';
                modal.querySelector('#authPassword').setAttribute('autocomplete', m === 'login' ? 'current-password' : 'new-password');
                modal.querySelector('#authErr').textContent = '';
            }
            function openModal(m) { if (!modal) buildModal(); setMode(m || 'login'); modal.classList.remove('hidden'); setTimeout(() => modal.querySelector('#authEmail').focus(), 30); }
            function closeModal() { if (modal) modal.classList.add('hidden'); }

            async function submit() {
                const email = modal.querySelector('#authEmail').value.trim();
                const password = modal.querySelector('#authPassword').value;
                const errEl = modal.querySelector('#authErr');
                const btn = modal.querySelector('#authSubmit');
                errEl.textContent = '';
                if (!email || !password) { errEl.textContent = 'Enter your email and password.'; return; }
                if (mode === 'register' && password.length < 8) { errEl.textContent = 'Password must be at least 8 characters.'; return; }
                btn.disabled = true;
                let signedIn = false;
                try {
                    const data = await api('POST', mode === 'login' ? '/api/auth/login' : '/api/auth/register', { email, password });
                    user = data.user;
                    signedIn = true;
                } catch (e) {
                    errEl.classList.remove('auth-ok');
                    errEl.textContent = e.status === 401 ? 'Invalid email or password.'
                        : e.status === 409 ? 'That email is already registered.' : (e.message || 'Something went wrong.');
                    btn.disabled = false;
                    return;
                }
                // Auth succeeded — confirm in-place, hydrate, then close. A render hiccup here
                // must NOT look like a failed login, so it's isolated from the auth try/catch.
                errEl.classList.add('auth-ok');
                errEl.textContent = mode === 'login' ? '✓ Signed in — loading your watchlists…' : '✓ Account created — welcome!';
                btn.textContent = '✓ Success';
                try { await onSignedIn(true); } catch (e) { console.warn('[auth] post-login render failed', e); }
                setTimeout(() => {
                    closeModal();
                    errEl.classList.remove('auth-ok'); errEl.textContent = '';
                    btn.disabled = false; btn.textContent = mode === 'login' ? 'Sign in' : 'Create account';
                }, 650);
            }

            async function logout() {
                try { await api('POST', '/api/auth/logout'); } catch (e) {}
                user = null;
                Watchlist.reset();
                renderUserButton();
                if (typeof _deps.renderWlListTabs === 'function') _deps.renderWlListTabs();
                if (typeof _deps.renderWatchlist === 'function') _deps.renderWatchlist();
                if (typeof _deps.updateWatchlistTabBadge === 'function') _deps.updateWatchlistTabBadge();
                if (typeof _deps.renderScreenerResults === 'function') _deps.renderScreenerResults();
            }

            // After a successful sign-in (or boot probe): hydrate lists, optionally migrate,
            // and repaint everything that reflects watchlist membership.
            async function onSignedIn(interactive) {
                try { await Watchlist.load(); } catch (e) { console.warn('[auth] watchlist load failed', e); }
                if (interactive) await maybeMigrate();
                renderUserButton();
                if (typeof _deps.renderWlListTabs === 'function') _deps.renderWlListTabs();
                if (typeof _deps.renderWatchlist === 'function') _deps.renderWatchlist();
                if (typeof _deps.updateWatchlistTabBadge === 'function') _deps.updateWatchlistTabBadge();
                if (typeof _deps.renderScreenerResults === 'function') _deps.renderScreenerResults();
            }

            // One-time import of the legacy localStorage watchlist into the default list.
            async function maybeMigrate() {
                try {
                    if (localStorage.getItem('abWatchlistMigratedV1') === '1') return;
                    const raw = localStorage.getItem('abWatchlistV1');
                    if (!raw) { localStorage.setItem('abWatchlistMigratedV1', '1'); return; }
                    const parsed = JSON.parse(raw);
                    const old = (parsed && Array.isArray(parsed.items)) ? parsed.items : [];
                    if (!old.length) { localStorage.setItem('abWatchlistMigratedV1', '1'); return; }
                    const fresh = old.filter(it => it && it.ticker && !Watchlist.has(it.ticker));
                    if (fresh.length && confirm(`Import ${fresh.length} saved ticker${fresh.length === 1 ? '' : 's'} from this browser into your account?`)) {
                        Watchlist.addMany(fresh.map(it => it.ticker));
                    }
                    localStorage.setItem('abWatchlistMigratedV1', '1');
                } catch (e) { console.warn('[auth] migration skipped', e); }
            }

            // ── Global-header account button + dropdown menu ─────────────────────────
            const esc = (s) => String(s == null ? '' : s).replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
            // Reflect signed-in/out state on the static header avatar button.
            function renderUserButton() {
                const btn = document.getElementById('userMenuBtn');
                if (!btn) return;
                if (user) {
                    const initial = (user.display_name || user.email || '?').trim().charAt(0).toUpperCase();
                    btn.textContent = initial || '?';
                    btn.classList.add('signed-in');
                    btn.title = user.email || 'Account';
                } else {
                    btn.classList.remove('signed-in');
                    btn.title = 'Sign in';
                    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:18px;height:18px"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6" stroke-linecap="round"/></svg>';
                }
            }
            function closeUserMenu() { const m = document.getElementById('userMenu'); if (m) m.remove(); document.removeEventListener('mousedown', userMenuOutside); }
            function userMenuOutside(e) { const m = document.getElementById('userMenu'); if (m && !m.contains(e.target) && e.target.id !== 'userMenuBtn' && !e.target.closest('#userMenuBtn')) closeUserMenu(); }
            function openUserMenu(btn) {
                if (document.getElementById('userMenu')) { closeUserMenu(); return; }
                const menu = document.createElement('div');
                menu.className = 'user-menu'; menu.id = 'userMenu';
                if (user) {
                    const isAdmin = user.role === 'admin';
                    menu.innerHTML =
                        `<div class="um-head">
                            <div class="um-email">${esc(user.display_name || user.email)}</div>
                            <div style="margin-top:6px">
                                <span class="um-badge tier">${esc(user.tier || 'free')}</span>
                                ${isAdmin ? '<span class="um-badge role-admin">admin</span>' : ''}
                            </div>
                         </div>
                         <button class="um-item um-disabled" disabled>Account settings <span style="margin-left:auto;font-size:10px;color:#475569">soon</span></button>
                         ${isAdmin ? '<button class="um-item" data-act="admin">Admin</button>' : ''}
                         <button class="um-item um-danger" data-act="logout">Log out</button>`;
                } else {
                    menu.innerHTML = `<button class="um-item" data-act="login">Sign in / Create account</button>`;
                }
                document.body.appendChild(menu);
                const r = btn.getBoundingClientRect();
                menu.style.top = (r.bottom + 8) + 'px';
                menu.style.left = Math.max(8, Math.min(r.right - menu.offsetWidth, window.innerWidth - menu.offsetWidth - 8)) + 'px';
                menu.addEventListener('click', (e) => {
                    const item = e.target.closest('[data-act]'); if (!item) return;
                    const act = item.dataset.act;
                    closeUserMenu();
                    if (act === 'login') openModal('login');
                    else if (act === 'logout') logout();
                    else if (act === 'admin') alert('Admin panel coming soon. (' + (Auth.getUser() ? Auth.getUser().email : '') + ')');
                });
                setTimeout(() => document.addEventListener('mousedown', userMenuOutside), 0);
            }

            async function boot() {
                if (!modal) buildModal();
                const sb = document.getElementById('wlSigninBtn'); if (sb) sb.addEventListener('click', () => openModal('login'));
                const umb = document.getElementById('userMenuBtn'); if (umb) umb.addEventListener('click', (e) => { e.stopPropagation(); openUserMenu(umb); });
                try {
                    const data = await api('GET', '/api/auth/me');
                    user = (data && data.user) || null;
                } catch (e) { user = null; }
                renderUserButton();
                if (user) await onSignedIn(false);
                else { if (typeof _deps.renderWlListTabs === 'function') _deps.renderWlListTabs(); if (typeof _deps.renderWatchlist === 'function') _deps.renderWatchlist(); }
            }

            return { isAuthed, getUser, openModal, closeModal, logout, boot };
})();

if (typeof window !== 'undefined') {
    window.Auth = Auth;                                  // preserve the singleton global (external callers)
    window.auth = { configure, deps: () => _deps };      // module DI/verification surface
}

